import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { StoreProvider, useStore } from "@/context/StoreContext";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TelegramButton from "@/components/SupportDock";
import MarketModal from "@/components/MarketModal";
import AuthModal from "@/components/AuthModal";
import Home from "@/pages/Home";
import Market from "@/pages/Market";
import HowItWorks from "@/pages/HowItWorks";
import Support from "@/pages/Support";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Orders from "@/pages/Orders";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";
import ResetPassword from "@/pages/ResetPassword";
import { AML, DELIVERY, PRIVACY, REFUND, TERMS } from "@/legal/resellerDocuments";

let lenisInstance = null;

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenisInstance) lenisInstance.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Splash = ({ failed }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background">
    <span className="block h-4 w-4 rotate-45 rounded-[3px] bg-primary shadow-[0_0_24px_rgba(0,240,255,0.5)]" />
    <p className="font-display text-xs uppercase tracking-[0.4em] text-muted-foreground">
      {failed ? "Store failed to load — refresh" : "Zeyinx"}
    </p>
  </div>
);

const Shell = () => {
  const store = useStore();
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisInstance = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisInstance = null; };
  }, []);
  if (!store.ready) return <Splash failed={store.failed} />;
  return (
    <div className="App min-h-screen bg-background">
      <ScrollManager />
      <div className="noise-overlay" />
      <Header />
      <main className="relative z-[2]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/cards" element={<Market />} />
          <Route path="/market/:marketSlug" element={<Market />} />
          <Route path="/market/:marketSlug/:categorySlug" element={<Market />} />
          <Route path="/search" element={<Market />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/support" element={<Support />} />
          <Route path="/product/:id" element={<Product />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account/orders" element={<Orders />} />
          <Route path="/account/profile" element={<Profile />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/products" element={<Admin />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<LegalPage doc={TERMS} />} />
          <Route path="/privacy" element={<LegalPage doc={PRIVACY} />} />
          <Route path="/refund" element={<LegalPage doc={REFUND} />} />
          <Route path="/aml" element={<LegalPage doc={AML} />} />
          <Route path="/delivery" element={<LegalPage doc={DELIVERY} />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
      <TelegramButton />
      <MarketModal />
      <AuthModal />
      <Toaster position="top-center" theme="dark" />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <StoreProvider>
        <AuthProvider>
          <CartProvider>
            <Shell />
          </CartProvider>
        </AuthProvider>
      </StoreProvider>
    </BrowserRouter>
  );
}

export default App;
